import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  useAuth,
  useI18N,
  usePostAddMapperNotes,
  VenueDetailsBySlugResponse,
} from "common/hooks";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon } from "lucide-react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import SelectDropDown from "@/components/common/SelectDropDown";

function AddMapperNote(props: {
  venueDetailsReponse: VenueDetailsBySlugResponse;
  onClose: () => void;
  onInfoClick: () => void;
}) {
  const i18n = useI18N();
  const { user } = useAuth();

  const showToast = useCustomToast();

  const [notes, setNotes] = useState("");
  const { data: mutateResult, error, status, mutate } = usePostAddMapperNotes();
  const [selectedOption, setSelectedOption] = useState(0);
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [returnOnDate, setReturnOnDate] = useState("");
  const [actionOnDate, setActionOnDate] = useState("");
  const [selectedArea, setSelectedArea] = useState<{ parentId: number; subId: string } | null>(null);

  const selectedAreaDetails = useMemo(() => {
    if (selectedArea) {
      const section = props.venueDetailsReponse.details.businessVenueNotesAreaLocations.find(
        (e) => e.id.toString() === selectedArea.parentId.toString()
      );
      const subSection = section?.tags.find(
        (e) => e.id.toString() === selectedArea.subId
      );
      return {
        parentId: selectedArea.parentId,
        parentName: section?.name,
        subId: selectedArea.subId,
        subName: subSection?.name,
        location_type: section?.location_type,
      };
    }
    return undefined;
  }, [selectedArea, props.venueDetailsReponse.details.businessVenueNotesAreaLocations]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("mapperNoteReportError");
      AnalyticsService.trackMx("mapperNoteReportError", {
        VenueId: props.venueDetailsReponse.details.id,
        VenueBrandId: props.venueDetailsReponse.details.brandId,
        VenueBrandName: props.venueDetailsReponse.details.brandName,
        VenueAddress: props.venueDetailsReponse.details.address,
        VenueName: props.venueDetailsReponse.details.name,
        VenueTypes: props.venueDetailsReponse.details.types,
      });

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {
      if (selectedOption === 1 || selectedOption === 6) {
        const urlObj =
          props.venueDetailsReponse.details.businessNotesActions
          ?.filter(
            (e) => e.id === selectedOption
          );
        if (urlObj.length > 0) {
          AnalyticsService.trackGAEventHandler("mapperNoteExternalLink");
          AnalyticsService.trackMx("mapperNoteExternalLink", {
            VenueId: props.venueDetailsReponse.details.id,
            VenueBrandId: props.venueDetailsReponse.details.brandId,
            VenueBrandName: props.venueDetailsReponse.details.brandName,
            VenueAddress: props.venueDetailsReponse.details.address,
            VenueName: props.venueDetailsReponse.details.name,
            VenueTypes: props.venueDetailsReponse.details.types,
          });

          // console.log("urlObj", urlObj);
          // Open the first URL in a new tab
          window.open((urlObj[0] as any).external_link, "_blank"); // Assuming urlObj[0] has a 'url' property
        }
      }
      console.log("mutateResult", mutateResult);
      showToast(i18n.get("mapperNoteAdded"), "success");
      setNotes("");
      setSelectedOption(0);
      setSelectedArea(null);
      props.onClose();
    }
  }, [mutateResult]);

  const submit = () => {
    AnalyticsService.trackGAEventHandler("mapperNoteReportCreate");
    AnalyticsService.trackMx("mapperNoteReportCreate", {
      VenueId: props.venueDetailsReponse.details.id,
      VenueBrandId: props.venueDetailsReponse.details.brandId,
      VenueBrandName: props.venueDetailsReponse.details.brandName,
      VenueAddress: props.venueDetailsReponse.details.address,
      VenueName: props.venueDetailsReponse.details.name,
      VenueTypes: props.venueDetailsReponse.details.types,
      ReviewerName: user?.name,
    });

    const locationId = selectedAreaDetails?.subId
      ? selectedAreaDetails?.subId
      : selectedAreaDetails?.parentId?.toString();
    const locationType = selectedAreaDetails?.location_type || "";

    mutate({
      venueId: props.venueDetailsReponse.details?.business?.id.toString() || "",
      note: notes,
      businessReportIssueAction: selectedOption.toString(),
      returnOnDate: returnOnDate,
      noteAreaId: locationId,
      noteAreaType: locationType,
      actionOnDate: actionOnDate,
    });
  };

  const handleOptionPress = (id: number) => {
    console.log("handleOptionPress", id);
    setSelectedOption(selectedOption === id ? 0 : id);

    console.log("handleOptionPress selectedOption", selectedOption);

    // Reset Return on Date if option is not 8
    if (id !== 8) {
      setReturnOnDate("");
    }

    // Reset selected area if not "Incomplete venue"
    if (id !== 4) { // 4 is the ID for "Incomplete venue"
      setSelectedArea(null);
    }
  };

  const isSubmitDisabled = () => {
    // Disable if option 8 is selected but no return date is set
    if (selectedOption === 8 && !returnOnDate) return true;

    // Disable if "Incomplete venue" is selected but no area is selected
    if (selectedOption === 4 && !selectedArea) return true;

    // Additional conditions for other options
    return selectedOption === 0 || notes.length < 5;
  };


  return (
    <>
      <section
        className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-200px)] overflow-y-auto"
        role="none"
      >
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <h1
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            tabIndex={0}
          >
            {i18n.get("createNote")}
          </h1>
          <button
            onClick={props.onClose}
            className="absolute top-4 left-4"
            aria-label="Close modal"
          >
            <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
          </button>
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3 px-3"
          role="none"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div
                  className="self-stretch !text-primary-500 dark:!text-primary-300 paragraph-xsmall-semibold"
                  tabIndex={0}
                >
                  {props.venueDetailsReponse.details.name}
                </div>
                <div
                  className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold"
                  tabIndex={0}
                >
                  {i18n.get("mapperNote")}
                </div>
              </div>
              <div
                className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold pt-1"
                tabIndex={0}
              >
                {i18n.get("shareYourInsightsToHelp")}
              </div>

            </div>
            <div className="self-stretch   rounded-lg justify-start items-center gap-8 ">
              <div className="mb-2">
                <div className="flex flex-col mt-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SelectDropDown
                        label={i18n.get("selectNoteStatus")}
                        optionsList={[
                          { id: 0, label: i18n.get("selectStatus") },
                          ...(props.venueDetailsReponse.details.businessNotesActions
                            || []).map(
                            (option) => ({
                              id: option.id,
                              label: option.title,
                            })
                          ),
                        ]}
                        value={selectedOption}
                        required={true} 
                        onChange={(e) => handleOptionPress(Number(e.target.value))}
                      />
                    </div>
                    <img
                      src={isDark ? "/images/Info_dark.svg" : "/images/Info.svg"}
                      alt=""
                      aria-hidden="true"
                      className="w-6 h-6 self-start mt-3 cursor-pointer"
                      onClick={props.onInfoClick}
                    />
                  </div>
                </div>

                {/* Area Selection - Only shown when "Incomplete venue" is selected */}
                {selectedOption === 4 && (
                  <div className="mt-4">
                    <SelectDropDown
                      label={i18n.get("selectNoteLocation")}
                      optionsList={[
                        { id: "0", label: i18n.get("selectArea") },
                        ...props.venueDetailsReponse.details.businessVenueNotesAreaLocations
                          .filter((area) => area.id !== "Business")
                          .map((area) => ({
                            id: area.id.toString(),
                            label: area.name,
                            subTitles: area.tags.map((tag) => ({
                              id: tag.id.toString(),
                              label: tag.name,
                            })),
                          })),
                      ]}
                      value={selectedArea ? `${selectedArea.parentId}.${selectedArea.subId}` : "0"}
                      required={true}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "0") {
                          setSelectedArea(null);
                          return;
                        }
                        const [parentId, subId] = value.split('.');
                        if (parentId && subId) {
                          setSelectedArea({
                            parentId: Number(parentId),
                            subId
                          });
                        }
                      }}
                    />
                  </div>
                )}

                {/* Conditional Rendering of Return on Date */}
                {selectedOption === 8 && (
                  <div className="my-4">
                    <label
                      htmlFor="returnOnDate"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {i18n.get("returnToVenueOnDate")}
                    </label>
                    <input
                      type="datetime-local"
                      id="returnOnDate"
                      value={returnOnDate}
                      onChange={(e) => setReturnOnDate(e.target.value)}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm dark:border-gray-500"
                      min={new Date().toISOString().split("T")[0] + "T00:00"} // Minimum is today's date, starting at 00:00
                      max={
                        new Date(new Date().getTime() + 14 * 24 * 60 * 60 * 1000)
                          .toISOString()
                          .split("T")[0] + "T23:59" // Maximum is 5 days from today, ending at 23:59
                      }
                    />
                  </div>
                )}

                {/* Conditional Rendering of Venue part of a client project */}
                {selectedOption === 15 && (
                  <div className="my-4">
                    <label
                      htmlFor="actionOnDate"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {i18n.get("makeVenueVisibleToMappersUntil")}
                    </label>
                    <input
                      type="datetime-local"
                      id="actionOnDate"
                      value={actionOnDate}
                      onChange={(e) => setActionOnDate(e.target.value)}
                      className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm dark:border-gray-500"
                      min={new Date().toISOString().split("T")[0] + "T00:00"} // Minimum is today's date, starting at 00:00                    
                    />
                  </div>
                )}

              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full " tabIndex={0}>
                <Textarea
                  id="review"
                  placeholder={i18n.get("addNotesHere")}
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                />
                <div
                  id="charCount"
                  className="button-small-medium text-neutral-500 mt-3"
                  tabIndex={0}
                >
                  <span className={notes.length < 5 ? "text-red-600" : ""}>
                    {notes.length}
                  </span>
                  /150 Characters - at least 5 characters required
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex-col justify-end items-center gap-4 flex w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit Mapper note"
              >
                {i18n.get("submit")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default AddMapperNote;
